import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Logo from '../../components/Logo';
import SEO from '../../components/SEO';

// Hook pour détecter si un élément est visible
const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

// Composant pour animation au scroll
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isInView] = useInView(0.1);
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

const SparringPartner = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // SEO Configuration
  const seoConfig = {
    title: 'Sparring Partner Stratégique - FOMOW | Conseil Dirigeants Commerciaux',
    description: 'Mentorat stratégique pour CEO, CRO et directeurs commerciaux. Sessions de sparring individuelles pour clarifier votre vision, prioriser et accélérer vos décisions business.',
    path: '/services/sparring',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Sparring Partner Stratégique",
      "description": "Accompagnement stratégique individuel pour dirigeants commerciaux",
      "provider": { "@type": "Organization", "name": "FOMOW" },
      "serviceType": "Business Consulting"
    }
  };

  // Effet de parallaxe sur le hero
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const benefits = [
    { 
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: 'Prendre du recul sur votre business',
      desc: 'Sortir de l\'opérationnel pour voir plus clair et prioriser ce qui compte vraiment.'
    },
    { 
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Être challengé sans complaisance',
      desc: 'Un regard extérieur exigeant qui vous pousse à aller plus loin.'
    },
    { 
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      ),
      title: 'Avancer avec des méthodes terrain',
      desc: 'Des conseils concrets issus de notre expérience avec 30+ dirigeants.'
    },
  ];

  const engagementDetails = [
    { label: 'Durée', value: 'Engagement de 12 mois, flexible et résiliable à tout moment avec 1 mois de préavis.' },
    { label: 'Disponibilité', value: 'Accès direct par message entre les sessions' },
    { label: 'Suivi', value: 'Compte-rendu après chaque session + actions à mener' },
  ];

  const deliverables = [
    'Session de travail hebdomadaire sur vos priorités',
    'Challenge de vos décisions et hypothèses',
    'Aide au recrutement de vos futurs commerciaux',
    'Accès à nos templates et playbooks sales',
    'Réseau de contacts (investisseurs, experts, pairs)',
  ];

  const faqs = [
    { 
      q: 'C\'est quoi exactement un Sparring Partner ?', 
      a: 'C\'est un partenaire de réflexion dédié qui vous challenge sur vos décisions commerciales. Contrairement à un coach, on travaille ensemble sur vos vrais problèmes avec une approche opérationnelle.' 
    },
    { 
      q: 'À quelle fréquence on se voit ?', 
      a: 'Une session d\'1h par semaine, à heure fixe. C\'est le rythme idéal pour maintenir le momentum sans surcharger votre agenda. On reste disponibles entre les sessions si besoin.' 
    },
    { 
      q: 'C\'est adapté à mon stade de développement ?', 
      a: 'Le Sparring Partner est idéal si vous êtes CEO, fondateur ou directeur commercial avec des enjeux de croissance : structurer votre équipe, améliorer votre process de vente, préparer une levée...' 
    },
    { 
      q: 'Quelle différence avec un consultant ?', 
      a: 'Un consultant vous livre un rapport. Nous, on travaille avec vous chaque semaine sur le long terme. On ne donne pas de conseils théoriques : on vous aide à prendre les décisions et à les exécuter.' 
    },
    { 
      q: 'Combien de temps dure l\'accompagnement ?', 
      a: 'Pas de durée minimum imposée. Nos clients restent en moyenne 6 à 12 mois, le temps de structurer leur croissance. Vous arrêtez quand vous voulez, avec un mois de préavis.' 
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen overflow-hidden">
      <SEO 
        title={seoConfig.title}
        description={seoConfig.description}
        path={seoConfig.path}
        structuredData={seoConfig.structuredData}
      />
      <Navbar />
      
      {/* Hero Section avec effet parallaxe */}
      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-br from-[#041C2C] via-[#0a2a3d] to-[#041C2C] overflow-hidden">
        {/* Cercles décoratifs animés */}
        <div 
          className="absolute top-20 right-10 w-72 h-72 bg-[#2A41E5]/10 rounded-full blur-3xl"
          style={{ transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)` }}
        />
        <div 
          className="absolute bottom-10 left-10 w-96 h-96 bg-[#2A41E5]/5 rounded-full blur-3xl"
          style={{ transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)` }}
        />
        
        {/* Grille animée en arrière-plan */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection delay={100}>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 bg-[#2A41E5] rounded-full mr-2 animate-pulse" />
              <span className="text-sm text-[#2A41E5] font-medium">Sparring Partner</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Stratégie & Sparring Partner
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={300}>
            <p className="text-xl md:text-2xl text-white/70 max-w-3xl leading-relaxed mb-4">
              Mentorat stratégique individuel et réunions de travail pour les 
              <span className="text-[#2A41E5] font-semibold"> dirigeants en phase de croissance</span>.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={400}>
            <p className="text-lg text-white/50 max-w-2xl mb-10">
              Tout fondateur, CEO ou directeur commercial a besoin d'une personne qu'il peut challenger et par qui il peut être challengé.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={500}>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 hover:scale-105"
            >
              Contactez-nous
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </AnimatedSection>
        </div>

        {/* Flèche animée pour scroller */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Section: Un partenaire de confiance */}
      <section id="details" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-4 block">
                Notre approche
              </span>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Votre allié stratégique
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                En tant que dirigeant, vous êtes souvent seul face aux décisions commerciales importantes. <strong>Manque de temps, urgences quotidiennes, personne pour vous challenger</strong> : difficile de prendre du recul.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Avec FOMOW, vous bénéficiez d'un <strong>échange hebdomadaire</strong> avec un expert qui connaît vos enjeux, pose les bonnes questions et vous aide à <strong>trancher plus vite</strong>.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-6">
                {benefits.map((benefit, idx) => (
                  <div 
                    key={idx}
                    className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-[#2A41E5]/10 flex items-center justify-center text-[#2A41E5] group-hover:bg-[#2A41E5] group-hover:text-white transition-all duration-300 flex-shrink-0">
                        {benefit.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#2A41E5] transition-colors">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Section: Format d'engagement */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C] relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-4 block">
                Comment ça marche
              </span>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
                Un cadre clair, des résultats concrets
              </h2>
              <p className="text-lg text-white/70 max-w-3xl mx-auto">
                <strong className="text-white">Pas de blabla, que du concret.</strong> Un rendez-vous fixe chaque semaine pour avancer sur vos vrais sujets, avec un suivi rigoureux entre les sessions.
              </p>
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto space-y-6">
            {/* Durée - en haut */}
            <AnimatedSection delay={100}>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="text-[#2A41E5] text-sm font-semibold uppercase tracking-wider mb-2">Durée</div>
                <div className="text-white text-lg">{engagementDetails[0].value}</div>
              </div>
            </AnimatedSection>

            {/* Nos actions - grand bloc central */}
            <AnimatedSection delay={200}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-6">Nos actions</h3>
                <ul className="space-y-4">
                  {deliverables.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 group">
                      <span className="w-6 h-6 rounded-full bg-[#2A41E5]/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#2A41E5] transition-colors duration-300">
                        <svg className="w-3 h-3 text-[#2A41E5] group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-white/80 group-hover:text-white transition-colors">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>

            {/* Disponibilité - en bas */}
            <AnimatedSection delay={300}>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="text-[#2A41E5] text-sm font-semibold uppercase tracking-wider mb-2">Disponibilité</div>
                <div className="text-white text-lg">{engagementDetails[1].value}</div>
              </div>
            </AnimatedSection>

            {/* Suivi - en bas */}
            <AnimatedSection delay={400}>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="text-[#2A41E5] text-sm font-semibold uppercase tracking-wider mb-2">Suivi</div>
                <div className="text-white text-lg">{engagementDetails[2].value}</div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Section CTA - Développez-vous avec confiance */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#041C2C] via-[#0a2a3d] to-[#041C2C] relative overflow-hidden">
        {/* Formes géométriques décoratives */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#2A41E5]/10 to-transparent" />
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#041C2C] to-transparent" />
          {/* Lignes diagonales subtiles */}
          <div className="absolute top-20 right-20 w-64 h-64 border border-white/5 rotate-12 rounded-3xl" />
          <div className="absolute bottom-20 right-40 w-48 h-48 border border-white/5 -rotate-12 rounded-3xl" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-8 leading-tight">
              Arrêtez de décider seul
            </h2>
            
            <div className="space-y-4 mb-10">
              <p className="text-lg text-white font-medium">
                Les meilleurs dirigeants s'entourent.
              </p>
              <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
                Pas d'un consultant qui vous livre un rapport. Pas d'un coach qui vous pose des questions sans réponse. Mais d'un partenaire qui a vécu vos galères et sait comment en sortir.
              </p>
              <p className="text-lg text-white/70">
                Avec FOMOW, vous gagnez en <strong className="text-white">clarté, en vitesse et en sérénité</strong>.
              </p>
            </div>

            <div className="inline-flex items-center gap-2">
              <a
                href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-l-xl hover:bg-[#1E32B8] transition-all duration-300"
              >
                COMMENCER
              </a>
              <a
                href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-14 h-14 bg-white text-[#2A41E5] rounded-r-xl hover:bg-gray-100 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
                Foire aux questions
              </h2>
              <p className="text-gray-600">Tout ce que vous devez savoir sur notre accompagnement Sparring Partner</p>
            </div>
          </AnimatedSection>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 50}>
                <div 
                  className={`bg-[#F5F5F5] rounded-2xl border transition-all duration-300 overflow-hidden ${openFaq === idx ? 'border-[#2A41E5]/30 shadow-lg' : 'border-transparent hover:border-gray-200'}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left group"
                  >
                    <span className="font-semibold text-gray-900 pr-4 group-hover:text-[#2A41E5] transition-colors">{faq.q}</span>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${openFaq === idx ? 'bg-[#2A41E5] rotate-180' : 'bg-gray-200 group-hover:bg-[#2A41E5]/20'}`}>
                      <svg 
                        className={`w-4 h-4 transition-colors ${openFaq === idx ? 'text-white' : 'text-gray-500'}`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="contact" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#041C2C] via-[#0a2a3d] to-[#041C2C] relative overflow-hidden">
        {/* Éléments décoratifs */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-[#2A41E5]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-[#2A41E5]/10 rounded-full blur-2xl" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              Prêt à accélérer ?
            </h2>
            <p className="text-xl text-white/70 mb-4 max-w-2xl mx-auto">
              <strong className="text-white">Réservez un appel de 30 minutes.</strong>
            </p>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              Analysons vos besoins pour valider la pertinence d'un accompagnement.<br />Sans engagement.
            </p>
            <a
              href="https://calendly.com/fomow"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 px-10 py-5 bg-[#2A41E5] text-white font-bold text-lg rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-xl hover:shadow-[#2A41E5]/30 hover:scale-105"
            >
              Réserver un appel
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Services liés */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-3 block">
                Découvrez aussi
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Nos autres accompagnements</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Link 
                to="/services/coach" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Coaching & Excellence Commerciale</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Formez vos équipes aux techniques de vente qui marchent. La vente, c'est du sport : on les entraîne.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
              
              <Link 
                to="/services/accelerateur" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Accélération & Scaling Business</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">On prospecte, vous closez. 20-30 RDV qualifiés par mois avec vos cibles.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">&copy; 2024 FOMOW. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default SparringPartner;
